// Seed data for the messages mock transport (see messagesApi.js). Mirrors
// the reference product screenshots so the feature reads as a believable
// messaging product rather than lorem-ipsum placeholders.
//
// messagesMockData.js: static mock/fixture data, not an API service. Only
// CURRENT_USER is still consumed by the active messagesService.js, as a
// fallback identity used when there's no authenticated user (see
// getCurrentUser there); the rest of this file's exports back the
// now-dead mock transport in src/services/apis/messagesApi.js.

/** Fallback identity used as "me" when there is no authenticated user. */
export const CURRENT_USER = {
  id: 'me',
  name: 'Alex Williams',
  initials: 'AW',
  presence: 'online',
};

/** Fixed roster of mock conversation participants (name, initials, presence). */
export const MOCK_USERS = [
  { id: 'u-priya', name: 'Priya Sharma', initials: 'PS', presence: 'online' },
  { id: 'u-jordan', name: 'Jordan Lee', initials: 'JL', presence: 'away' },
  { id: 'u-sofia', name: 'Sofia Reyes', initials: 'SR', presence: 'busy' },
  { id: 'u-marcus', name: 'Marcus Chen', initials: 'MC', presence: 'offline' },
];

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

function userById(id) {
  const user = MOCK_USERS.find((u) => u.id === id);
  if (!user) throw new Error(`messagesMockData: unknown user id "${id}"`);
  return user;
}

/** Fixed list of mock conversations, one per MOCK_USERS entry, with a preview and unread count. */
export const MOCK_CONVERSATIONS = [
  {
    id: 'c-priya',
    participant: userById('u-priya'),
    lastMessagePreview: 'Can we catch up tomorrow?',
    lastMessageAt: Date.now() - 8 * MINUTE,
    unreadCount: 2,
  },
  {
    id: 'c-jordan',
    participant: userById('u-jordan'),
    lastMessagePreview: "I'll send the design files by EOD",
    lastMessageAt: Date.now() - 90 * MINUTE,
    unreadCount: 0,
  },
  {
    id: 'c-sofia',
    participant: userById('u-sofia'),
    lastMessagePreview: 'The meeting is at 3 PM!',
    lastMessageAt: Date.now() - DAY,
    unreadCount: 1,
  },
  {
    id: 'c-marcus',
    participant: userById('u-marcus'),
    lastMessagePreview: 'Thanks for the feedback \u{1F64F}',
    lastMessageAt: Date.now() - 3 * DAY,
    unreadCount: 0,
  },
];

let seq = 0;
function nextId(prefix) {
  seq += 1;
  return `${prefix}-${seq}`;
}

function seedThread(conversationId, otherUserId, lines) {
  return lines.map((line) => ({
    id: nextId('m'),
    conversationId,
    senderId: line.from === 'me' ? CURRENT_USER.id : otherUserId,
    text: line.text,
    createdAt: Date.now() - line.minutesAgo * MINUTE,
    status: 'read',
  }));
}

/** Mock message threads keyed by conversation id, generated via seedThread. */
export const MOCK_MESSAGES = {
  'c-priya': seedThread('c-priya', 'u-priya', [
    { from: 'them', text: 'Hey! How are you doing?', minutesAgo: 20 },
    { from: 'me', text: "I'm great, thanks! Working on a new project.", minutesAgo: 17 },
    { from: 'them', text: 'That sounds exciting! What kind of project?', minutesAgo: 14 },
    { from: 'me', text: 'A social media app — it’s coming together nicely \u{1F680}', minutesAgo: 11 },
    { from: 'them', text: 'Can we catch up tomorrow?', minutesAgo: 8 },
  ]),
  'c-jordan': seedThread('c-jordan', 'u-jordan', [
    { from: 'me', text: 'Hey Jordan, any update on the mockups?', minutesAgo: 130 },
    { from: 'them', text: 'Almost done, just polishing the empty states.', minutesAgo: 100 },
    { from: 'them', text: "I'll send the design files by EOD", minutesAgo: 90 },
  ]),
  'c-sofia': seedThread('c-sofia', 'u-sofia', [
    { from: 'them', text: 'Don’t forget the sync tomorrow.', minutesAgo: DAY / MINUTE + 40 },
    { from: 'them', text: 'The meeting is at 3 PM!', minutesAgo: DAY / MINUTE },
  ]),
  'c-marcus': seedThread('c-marcus', 'u-marcus', [
    { from: 'me', text: 'Left some comments on your PR, mostly nits.', minutesAgo: 3 * (DAY / MINUTE) + 30 },
    { from: 'them', text: 'Thanks for the feedback \u{1F64F}', minutesAgo: 3 * (DAY / MINUTE) },
  ]),
};

/** Canned replies the mock transport can pick from to simulate the other side responding. */
export const AUTO_REPLIES = [
  'Got it, thanks!',
  'Sounds good to me.',
  "Let me get back to you on that.",
  'Haha, true.',
  'Can you send more details?',
  '\u{1F44D}',
];
